import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {pageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery} from '@github-ui/react-query'
import {Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {reactFetchJSON} from '@github-ui/verified-fetch'

const StatusChecksResponseSchema = Type.Object({
  statusRollup: Type.Object({
    pendingWorkflowApprovalRollup: Type.Union([
      Type.Object({
        viewerCanApproveWorkflowRuns: Type.Boolean(),
        hasExpiredWorkflowRuns: Type.Boolean(),
        approvalRequiredMessage: Type.String(),
      }),
      Type.Null(),
    ]),
  }),
})

type StatusChecksResponse = Type.Static<typeof StatusChecksResponseSchema>

const StatusChecksResponseValidator = Compile(StatusChecksResponseSchema)

export function useWorkflowApprovalText({basePageDataUrl, enabled}: {basePageDataUrl: string; enabled: boolean}) {
  const statusChecksUrl = pageDataUrl(basePageDataUrl, PageData.statusChecks)
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Permission state must refetch rather than persist; TypeBox validates this JSON endpoint because it has no protobuf schema.
  const {data: statusChecksData} = useQuery({
    queryKey: ['assign-to-copilot-workflow-approval', statusChecksUrl],
    queryFn: async ({signal}): Promise<StatusChecksResponse> => {
      const response = await reactFetchJSON(statusChecksUrl, {signal})
      if (!response.ok) throw new Error('Failed to fetch workflow approval status')
      const body = (await response.json()) as unknown
      if (!StatusChecksResponseValidator.Check(body)) throw new Error('Malformed workflow approval status response')
      return body
    },
    enabled,
    throwOnError: false,
  })

  const pendingWorkflowApproval = statusChecksData?.statusRollup.pendingWorkflowApprovalRollup
  if (!pendingWorkflowApproval) return null

  if (pendingWorkflowApproval.hasExpiredWorkflowRuns) {
    return 'Unable to re-run one or more workflows because they were created over a month ago.'
  }

  return pendingWorkflowApproval.viewerCanApproveWorkflowRuns
    ? 'Approve workflows to verify this fix.'
    : 'A maintainer must approve the workflows to verify this fix.'
}
